import Hero from "@/components/Hero";
import HeroStatsStrip from "@/components/HeroStatsStrip";
import AccommodationGrid from "@/components/AccommodationGrid";
import AmenitiesBento from "@/components/AmenitiesBento";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import MapSection from "@/components/MapSection";
import NearbySection from "@/components/NearbySection";
import Reveal from "@/components/Reveal";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { Link } from "react-router-dom";
import { ArrowRight, HouseLine, TreeEvergreen, Images, BookmarkSimple } from "@phosphor-icons/react";

const EXPLORE = [
  {
    to: "/accommodation",
    icon: HouseLine,
    label: "Accommodation",
    blurb: "Cabins, powered sites & camping.",
    testid: "explore-accommodation",
  },
  {
    to: "/amenities",
    icon: TreeEvergreen,
    label: "The park",
    blurb: "Pool, fire pits, amenities & more.",
    testid: "explore-amenities",
  },
  {
    to: "/gallery",
    icon: Images,
    label: "Gallery",
    blurb: "Real photos from the property.",
    testid: "explore-gallery",
  },
  {
    to: "/about",
    icon: BookmarkSimple,
    label: "Our story",
    blurb: "50 years under the gums.",
    testid: "explore-about",
  },
];

export default function HomePage() {
  useDocumentMeta();
  return (
    <>
      <Hero />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <HeroStatsStrip />
      </div>

      {/* Storyline strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-8" data-testid="story-strip">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="overline">A boutique caravan park in Ballarat</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mt-4">
                Bushland comfort, <em className="italic font-light">grown-up details.</em>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 text-sa-ink2 text-base sm:text-lg leading-relaxed">
              <p>
                Shady Acres is a quietly-run, family-owned caravan park in Warrenheip, Victoria — set on twelve acres of native gum forest directly on the M8 at the eastern gateway to Ballarat. The last decade has been invested in refining the bits that matter — wide, level{" "}
                <Link to="/accommodation" className="text-sa-accent hover:text-sa-accentHover">caravan sites</Link>, well-maintained{" "}
                <Link to="/accommodation" className="text-sa-accent hover:text-sa-accentHover">cabins in Ballarat</Link>, and softgrass camping — and the rest has been left pleasantly unhurried.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 mt-6 text-sa-accent hover:text-sa-accentHover" data-testid="home-about-link">
                Our story <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <AccommodationGrid />
      <AmenitiesBento />
      <NearbySection />
      <Testimonials />
      <FAQ />
      <MapSection />

      {/* Explore the park — bottom-of-page navigation nudge */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-28 pb-16" data-testid="explore-grid">
        <Reveal className="max-w-2xl mb-14 md:mb-16">
          <div className="overline">Explore the park</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mt-4">
            Keep <em className="italic font-light">wandering.</em>
          </h2>
          <p className="mt-5 text-sa-ink2 text-base sm:text-lg leading-relaxed">
            Four quick corners of the site if you&apos;re still deciding — or scroll down to book.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {EXPLORE.map((item, i) => {
            const Icon = item.icon;
            return (
              <Reveal key={item.to} delay={i * 70}>
                <Link
                  to={item.to}
                  data-testid={item.testid}
                  className="group flex flex-col justify-between h-full p-6 md:p-7 rounded-2xl border border-sa-ink/10 bg-white hover:border-sa-accent/60 hover:shadow-[0_10px_40px_-20px_rgba(198,111,85,0.35)] transition-all duration-300"
                >
                  <div>
                    <Icon size={28} weight="duotone" className="text-sa-accent" />
                    <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-5 text-sa-ink">{item.label}</h3>
                    <p className="mt-2 text-sa-ink2 text-sm leading-relaxed">{item.blurb}</p>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-sa-accent text-sm">
                    Visit
                    <ArrowRight size={14} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-32" data-testid="final-cta">
        <Reveal className="bg-sa-ink rounded-3xl p-12 md:p-20 text-center text-sa-bg relative overflow-hidden">
          <div className="overline text-sa-accent">Ready when you are</div>
          <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-tight mt-4 max-w-3xl mx-auto">
            Book direct, <em className="italic font-light">skip the queue.</em>
          </h2>
          <p className="mt-6 text-sa-bg/75 max-w-xl mx-auto">
            Direct bookings get our best rate, free pitch upgrades when available, and a personal welcome on arrival.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/book" className="btn-primary" data-testid="cta-book">
              Book a stay <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/contact" className="text-sa-bg hover:text-white underline underline-offset-8 decoration-sa-accent decoration-2 px-2 py-3" data-testid="cta-enquire">
              Or send an enquiry
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
